import React from 'react';
import {setFact} from '../api';
import './Form.css';
import {
  DateQuestion,
  FormItem,
  FormQuestion,
  MultipleChoiceQuestion,
  NumberQuestion,
  QuestionChoice,
  SingleChoiceQuestion,
  TextQuestion
} from '../rest';
import {CardDetails} from './Card';
import {confirmIfReplay} from "../App";
import _ from 'lodash';

export interface FormProps {
  readOnlyFields?: Array<string>;
  form: FormItem;
  isReplay: boolean;
  setForm: React.Dispatch<React.SetStateAction<FormItem | undefined>>
  setCardDetails: React.Dispatch<React.SetStateAction<CardDetails | undefined>>
}

const MIN_INT: number = -2147483648;
const MAX_INT: number = 2147483647;

function Form(props: FormProps) {

  const _setFact = (question: FormQuestion, value: any) => {
    const fact = {
      questionName: question.name,
      questionValue: value
    }
    // update form as well as possible without server
    let newForm = {...props.form};
    newForm.answers[fact.questionName] = fact.questionValue;
    props.setForm(newForm);

    confirmIfReplay(props.isReplay,
      async () => {
        let formFromServer = (await setFact(fact)).payload;
        // only if the async form of the server is different from the current client form, we set it
        // to avoid outdated renders with info we already have
        if (formFromServer.answers[fact.questionName] === fact.questionValue && !_.isEqual(props.form, formFromServer)) {
          props.setForm(formFromServer);
        }
      }
    );
  }

  const renderInfoButton = (question: SingleChoiceQuestion | MultipleChoiceQuestion | FormQuestion) => {

    const hasDescription = (descriptionObject: SingleChoiceQuestion | MultipleChoiceQuestion | FormQuestion | QuestionChoice) =>
      (descriptionObject.description && descriptionObject.description.length > 0)
      || ("questionChoices" in descriptionObject ? descriptionObject.questionChoices.some(hasDescription) : false);

    if (hasDescription(question)) {
      return <span className="info" onClick={() => {
        props.setCardDetails({
          name: question.name,
          description: question.description,
          choices: "questionChoices" in question
            ? question.questionChoices
              .filter(hasDescription)
              .map(c => ({
                name: c.prompt,
                description: c.description,
                choices: [],
              }))
            : [],
        })
      }}><svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path
        d="M0 0h24v24H0V0z" fill="none"/><path
        d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg></span>;
    }
  }

  const getInputRange = (question: NumberQuestion): string => {
    if (question.min === MIN_INT && question.max !== MAX_INT) {
      return "max " + question.max;
    } else if (question.min !== MIN_INT && question.max === MAX_INT) {
      return "min " + question.min;
    } else if (question.min !== MIN_INT && question.max !== MAX_INT) {
      return question.min + " - " + question.max;
    } else {
      return ""
    }
  }

  const renderDateQuestion = (index: number, question: DateQuestion) => {
    const value = props.form.answers[question.name];
    const readOnly = question.abstract || (props.readOnlyFields !== undefined && props.readOnlyFields.indexOf(question.name) > -1);
    return (
      <div className="form-group" key={index}>
        <div className="question question-date">
          <label className="question-label"
                 htmlFor={question.name}>{question.prompt} {renderInfoButton(question)}</label>
          <div/>
          <div className="answer">
            <input readOnly={readOnly} onChange={e => _setFact(question, new Date(e.target.value).toISOString())}
                   value={value === undefined ? '' : value}
                   id={question.name}
                   className="form-control"
                   type={"date"}/>
          </div>
        </div>
      </div>
    )
  }

  const renderTextQuestion = (index: number, question: TextQuestion) => {
    const value = props.form.answers[question.name];
    const readOnly = question.abstract || (props.readOnlyFields !== undefined && props.readOnlyFields.indexOf(question.name) > -1);
    return (
      <div className="form-group" key={index}>
        <div className="question question-text">
          <label className="question-label"
                 htmlFor={question.name}>{question.prompt} {renderInfoButton(question)}</label>
          <div/>
          <div className="answer">
            {question.largeTextArea
              ? <textarea readOnly={readOnly} onChange={e => _setFact(question, e.currentTarget.value)}
                          value={value === undefined ? '' : value}
                          id={question.name} className="form-control"/>
              : <input readOnly={readOnly} onChange={e => _setFact(question, e.currentTarget.value)}
                       value={value === undefined ? '' : value}
                       id={question.name} className="form-control"/>}
          </div>
        </div>
      </div>
    )
  }

  const renderNumberQuestion = (index: number, question: NumberQuestion) => {
    const value = props.form.answers[question.name];
    const readOnly = question.abstract || (props.readOnlyFields !== undefined && props.readOnlyFields.indexOf(question.name) > -1);
    const inputClass =
      value !== null &&
      value !== undefined &&
      ((value < question.min) || (value > question.max)) ?
        'form-control is-invalid' : 'form-control';
    return (
      <div className="form-group" key={index}>
        <div className="question question-number">
          <label className="question-label"
                 htmlFor={question.name}>{question.prompt} {renderInfoButton(question)}</label>
          <div/>
          <div className="answer">
            <input className={inputClass} readOnly={readOnly}
                   onChange={e => _setFact(question, +e.currentTarget.value)}
                   value={value === undefined ? '' : value} id={question.name}
                   placeholder={getInputRange(question)}
                   type={"number"} min={question.min} max={question.max}
                   step={question.stepSize}/>
            <span className="unit">{question.unit}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderSingleChoiceQuestion = (index: number, question: SingleChoiceQuestion) => {
    const readOnly = question.abstract || (props.readOnlyFields !== undefined && props.readOnlyFields.indexOf(question.name) > -1);
    return (
      <div className="form-group" key={index}>
        <div className="question question-choice">
          <label className="question-label"
                 htmlFor={question.name}>{question.prompt} {renderInfoButton(question)}</label>
          <div/>
          <span className={`btn-group answer answer-count-${question.questionChoices.length}`} role="group">
            {question.questionChoices.map((choice, indexKey) => {
              let key = question.name;
              return (
                <button disabled={readOnly}
                        onClick={() => _setFact(question, choice.name)}
                        className={`btn ${props.form.answers[key] === choice.name ? 'btn-info' : 'btn-light'}`}
                        key={indexKey + question.name}>{choice.prompt}</button>
              )
            })}
          </span>
        </div>
      </div>
    )
  }

  const renderMultipleChoiceQuestion = (index: number, question: MultipleChoiceQuestion) => {
    const readOnly = question.abstract || (props.readOnlyFields !== undefined && props.readOnlyFields.indexOf(question.name) > -1);
    return (
      <div className="form-group" key={index}>
        <div className="question question-choice">
          <label className="question-label"
                 htmlFor={question.name}>{question.prompt} {renderInfoButton(question)}</label>
          <div/>
          <span className={`btn-group answer answer-count-${question.questionChoices.length}`} role="group">
            {question.questionChoices.map((choice, indexKey) => {
              const key = question.name;
              const oldAnswers: Array<string> = props.form.answers[key] || [];

              return (
                <button disabled={readOnly}
                        onClick={() => _setFact(question, oldAnswers.indexOf(choice.name) !== -1 ? oldAnswers.filter(a => a !== choice.name) : [...oldAnswers, choice.name])}
                        className={`btn ${oldAnswers.indexOf(choice.name) !== -1 ? 'btn-info' : 'btn-light'}`}
                        key={indexKey + question.name}>{choice.name}</button>
              )
            })}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={"form"}>
      {props.form.questions.map((q, index) => {
        switch (q.kind) {
          case "NUMBER": {
            return renderNumberQuestion(index, q as NumberQuestion);
          }
          case "TEXT": {
            return renderTextQuestion(index, q as TextQuestion);
          }
          case "DATE": {
            return renderDateQuestion(index, q as DateQuestion);
          }
          case "SINGLECHOICE": {
            return renderSingleChoiceQuestion(index, q as SingleChoiceQuestion);
          }
          case "MULTIPLECHOICE": {
            return renderMultipleChoiceQuestion(index, q as SingleChoiceQuestion);
          }
          default: {
            return <div>Unbekannter Fragentyp: {q.kind}</div>
          }
        }
      })}
    </div>
  )


}

export default Form;